import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  Film, 
  Tv, 
  Radio, 
  Home, 
  X, 
  ArrowRight,
  TrendingUp,
  Sparkles
} from 'lucide-react';

const API_KEY = process.env.REACT_APP_API_KEY;
const BASE_URL = 'https://api.themoviedb.org/3';

const CommandPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasMovedSelection, setHasMovedSelection] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      setQuery('');
      setResults([]);
      setHasMovedSelection(false);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Debounced TMDB search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/search/multi?api_key=${API_KEY}&query=${encodeURIComponent(query)}&page=1`
        );
        const filtered = (res.data.results || [])
          .filter(item => item.media_type === 'movie' || item.media_type === 'tv')
          .slice(0, 6);
        setResults(filtered);
      } catch (err) {
        console.error('Command palette search error:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // Quick navigation items
  const navActions = [
    { id: 'nav-home', label: 'Go to Home', icon: Home, action: () => navigate('/') },
    { id: 'nav-movies', label: 'Browse Movies', icon: Film, action: () => navigate('/movies') },
    { id: 'nav-tv', label: 'Browse TV Shows', icon: Tv, action: () => navigate('/tv-shows') },
    { id: 'nav-livetv', label: 'Watch Live TV', icon: Radio, action: () => navigate('/livetv') },
  ];

  // Combined list for keyboard index selection
  const allListItems = [
    ...results.map(r => ({
      id: `media-${r.id}`,
      label: r.title || r.name,
      subtitle: `${r.media_type === 'movie' ? 'Movie' : 'TV Series'} • ${
        (r.release_date || r.first_air_date || '').slice(0, 4)
      }`,
      image: r.poster_path ? `https://image.tmdb.org/t/p/w92${r.poster_path}` : null,
      type: 'media',
      item: r
    })),
    ...navActions.map(n => ({
      id: n.id,
      label: n.label,
      type: 'navigation',
      icon: n.icon,
      action: n.action
    }))
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHasMovedSelection(true);
      setSelectedIndex(prev => (prev < allListItems.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHasMovedSelection(true);
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : allListItems.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();

      const trimmedQuery = query.trim();
      if (trimmedQuery && !hasMovedSelection) {
        navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
        onClose();
        return;
      }

      if (trimmedQuery && results.length === 0) {
        navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
        onClose();
        return;
      }

      const selected = allListItems[selectedIndex];
      if (selected) {
        if (selected.type === 'media') {
          const path = selected.item.media_type === 'movie' ? `/movie/${selected.item.id}` : `/tv-show/${selected.item.id}`;
          navigate(path);
        } else if (selected.action) {
          selected.action();
        }
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 bg-black/70 backdrop-blur-md animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl bg-[#121520]/90 border border-white/10 rounded-3xl shadow-2xl overflow-hidden glass-panel animate-scale-up"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search Header Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/5">
          <Search size={22} className="text-orange-500 flex-shrink-0 animate-pulse" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search movies, TV shows, or navigate..."
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setSelectedIndex(0);
              setHasMovedSelection(false);
            }}
            className="command-palette-input w-full py-2.5 h-full bg-transparent text-white placeholder-slate-400 text-lg font-medium outline-none focus:outline-none focus:ring-0"
          />
          {loading && (
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          )}
          <button 
            onClick={onClose}
            className="p-1 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Command Body */}
        <div className="max-h-[60vh] overflow-y-auto p-3 space-y-4 hide-scrollbar">
          {/* TMDB Results */}
          {results.length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-orange-400">
                <Sparkles size={13} /> Matches
              </div>
              <div className="mt-1 space-y-1">
                {results.map((item, idx) => {
                  const isSelected = selectedIndex === idx;
                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        const path = item.media_type === 'movie' ? `/movie/${item.id}` : `/tv-show/${item.id}`;
                        navigate(path);
                        onClose();
                      }}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? 'bg-orange-500/20 text-white border border-orange-500/30 shadow-sm' 
                          : 'text-slate-300 hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      {item.poster_path ? (
                        <img 
                          src={`https://image.tmdb.org/t/p/w92${item.poster_path}`}
                          alt=""
                          className="w-8 h-12 object-cover rounded-lg flex-shrink-0 shadow-sm"
                        />
                      ) : (
                        <div className="w-8 h-12 bg-white/10 rounded-lg flex items-center justify-center text-slate-500 flex-shrink-0">
                          {item.media_type === 'movie' ? <Film size={16} /> : <Tv size={16} />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{item.title || item.name}</div>
                        <div className="text-xs text-slate-400">
                          {item.media_type === 'movie' ? 'Movie' : 'TV Show'} • {
                            (item.release_date || item.first_air_date || '').slice(0, 4) || 'N/A'
                          } {item.vote_average ? `• ★ ${item.vote_average.toFixed(1)}` : ''}
                        </div>
                      </div>
                      <ArrowRight size={16} className={`transition-transform ${isSelected ? 'translate-x-1 text-orange-400' : 'opacity-0'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Navigations */}
          <div>
            <div className="flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <TrendingUp size={13} /> Quick Navigation & Actions
            </div>
            <div className="mt-1 space-y-1">
              {navActions.map((nav, idx) => {
                const globalIndex = results.length + idx;
                const isSelected = selectedIndex === globalIndex;
                const IconComponent = nav.icon;
                return (
                  <div
                    key={nav.id}
                    onClick={() => {
                      nav.action();
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                    className={`flex items-center gap-3 px-3.5 py-3 rounded-2xl cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'bg-orange-500/20 text-white border border-orange-500/30 shadow-sm' 
                        : 'text-slate-300 hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <div className={`p-2 rounded-xl ${isSelected ? 'bg-orange-500 text-white' : 'bg-white/5 text-slate-400'}`}>
                      <IconComponent size={18} />
                    </div>
                    <span className="font-medium text-sm flex-1">{nav.label}</span>
                    <ArrowRight size={16} className={`transition-transform ${isSelected ? 'translate-x-1 text-orange-400' : 'opacity-0'}`} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer shortcuts info */}
        <div className="px-5 py-3 border-t border-white/10 bg-black/40 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] text-white">↑↓</kbd> to navigate</span>
            <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] text-white">↵</kbd> to select</span>
            <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] text-white">ESC</kbd> to exit</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
